import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Button,
  Alert,
  Stack,
  Pagination
} from '@mui/material';
import {
  FilterList,
  Refresh,
  Info,
  Warning,
  CheckCircle,
  Error,
  QrCodeScanner,
  Inventory2,
  SwapHoriz
} from '@mui/icons-material';
import { fetchNotifications, normalizeNotificationPayload } from '../../services/notificationService';

const notificationLevels = [
  { value: 'warning', label: 'Warning' },
  { value: 'info', label: 'Info' }
];

const locationOptions = [
  { value: 'all', label: 'All Locations' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'store', label: 'Store' }
];

const createInitialDates = () => {
  const end = new Date();
  const start = new Date(end);
  end.setDate(end.getDate() + 1);
  start.setDate(start.getDate() - 7);
  return {
    start: start.toISOString().slice(0, 16),
    end: end.toISOString().slice(0, 16)
  };
};

const initialFilterState = {
  level: 'warning',
  locationType: 'all',
  startDate: createInitialDates().start,
  endDate: createInitialDates().end
};

const getNotificationIcon = (type) => {
  switch (type) {
    case 'rfid_scan':
      return QrCodeScanner;
    case 'inventory_change':
      return Inventory2;
    case 'transfer_complete':
    case 'import_complete':
      return SwapHoriz;
    case 'security_alert':
    case 'audit_alert':
      return Warning;
    case 'system_error':
      return Error;
    default:
      return Info;
  }
};

const getLevelColor = (level) => {
  switch (level) {
    case 'success':
      return 'success';
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
    case 'info':
    default:
      return 'info';
  }
};

const getLevelIcon = (level) => {
  switch (level) {
    case 'success':
      return CheckCircle;
    case 'warning':
      return Warning;
    case 'error':
      return Error;
    case 'info':
    default:
      return Info;
  }
};

const formatTimestamp = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const NotificationsList = () => {
  const [notifications, setNotifications] = useState([]);
  const [filterLevel, setFilterLevel] = useState(initialFilterState.level);
  const [locationFilter, setLocationFilter] = useState(initialFilterState.locationType);
  const [dateRange, setDateRange] = useState({
    start: initialFilterState.startDate,
    end: initialFilterState.endDate
  });
  const [appliedFilters, setAppliedFilters] = useState(initialFilterState);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const normalizedFilters = useMemo(() => ({
    ...appliedFilters,
    startDate: appliedFilters.startDate && !Number.isNaN(Date.parse(appliedFilters.startDate))
      ? new Date(appliedFilters.startDate).toISOString()
      : '',
    endDate: appliedFilters.endDate && !Number.isNaN(Date.parse(appliedFilters.endDate))
      ? new Date(appliedFilters.endDate).toISOString()
      : ''
  }), [appliedFilters]);

  const loadNotifications = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchNotifications({
        level: normalizedFilters.level,
        startDate: normalizedFilters.startDate || undefined,
        endDate: normalizedFilters.endDate || undefined
      });
      const items = normalizeNotificationPayload(response);
      setNotifications(items);
      setLastUpdated(new Date().toISOString());
    } catch (err) {
      setError(err.message || 'Unable to load notifications');
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  }, [normalizedFilters]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const filteredNotifications = useMemo(() => {
    return notifications.filter((notification) => {
      const level = (notification.level || 'info').toLowerCase();
      const locationType = (notification.location || notification.locationType || 'unknown').toLowerCase();

      const matchesLevel = level === appliedFilters.level;
      const matchesLocation = appliedFilters.locationType === 'all' || locationType === appliedFilters.locationType;

      return matchesLevel && matchesLocation;
    });
  }, [appliedFilters.level, appliedFilters.locationType, notifications]);

  const ITEMS_PER_PAGE = 100;
  const totalPages = Math.max(1, Math.ceil(filteredNotifications.length / ITEMS_PER_PAGE));
  const paginatedNotifications = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredNotifications.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [currentPage, filteredNotifications]);

  const handleRangeChange = (key, value) => {
    setDateRange((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({
      level: filterLevel,
      locationType: locationFilter,
      startDate: dateRange.start,
      endDate: dateRange.end
    });
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilterLevel(initialFilterState.level);
    setLocationFilter(initialFilterState.locationType);
    setDateRange({ start: initialFilterState.startDate, end: initialFilterState.endDate });
    setAppliedFilters(initialFilterState);
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    loadNotifications();
  };

  const handlePageChange = (_event, page) => {
    setCurrentPage(page);
  };

  return (
    <Box sx={{ px: { xs: 0, sm: 1 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: '#1e293b', mb: 1, fontSize: { xs: '1.5rem', sm: '2rem' } }}
        >
          System Notifications & Logs
        </Typography>
        <Typography variant="body1" sx={{ color: '#64748b' }}>
          Monitor RFID scans, inventory changes and automation events with live metadata.
        </Typography>
        {lastUpdated && (
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            Last updated {formatTimestamp(lastUpdated)}
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'center' }}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Level</InputLabel>
              <Select value={filterLevel} label="Level" onChange={(e) => setFilterLevel(e.target.value)}>
                {notificationLevels.map((level) => (
                  <MenuItem key={level.value} value={level.value}>
                    {level.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Location</InputLabel>
              <Select value={locationFilter} label="Location" onChange={(e) => setLocationFilter(e.target.value)}>
                {locationOptions.map((location) => (
                  <MenuItem key={location.value} value={location.value}>
                    {location.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Start"
              type="datetime-local"
              value={dateRange.start}
              onChange={(e) => handleRangeChange('start', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="End"
              type="datetime-local"
              value={dateRange.end}
              onChange={(e) => handleRangeChange('end', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              startIcon={<FilterList />}
              variant="contained"
              disableElevation
              onClick={handleApplyFilters}
            >
              Apply filters
            </Button>
            <Button variant="text" onClick={handleResetFilters}>
              Reset
            </Button>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={handleRefresh} disabled={isLoading}>
                  <Refresh />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" sx={{ color: '#475569' }}>
          Showing {paginatedNotifications.length} of {filteredNotifications.length} records
        </Typography>
        {filteredNotifications.length > ITEMS_PER_PAGE && (
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="small"
          />
        )}
      </Box>

      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ width: '15%' }}>Timestamp</TableCell>
                <TableCell sx={{ width: '10%' }}>Type</TableCell>
                <TableCell sx={{ width: '10%' }}>Level</TableCell>
                <TableCell sx={{ width: '10%' }}>Location</TableCell>
                <TableCell sx={{ width: '60%' }}>Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Loading notifications...
                  </TableCell>
                </TableRow>
              ) : paginatedNotifications.length ? (
                paginatedNotifications.map((notification) => {
                  const TypeIcon = getNotificationIcon(notification.type);
                  const LevelIcon = getLevelIcon(notification.level);
                  const resolvedLocation = notification.location || notification.locationName || '—';

                  return (
                    <TableRow
                      key={notification.id || notification._id || `${notification.createdAt}-${notification.serial}`}
                      className="hover:bg-gray-50"
                    >
                      <TableCell className="font-mono text-sm" sx={{ width: '6.25%' }}>
                        {formatTimestamp(notification.createdAt || notification.timestamp)}
                      </TableCell>
                      <TableCell sx={{ width: '6.25%' }}>
                        <Box className="flex items-center space-x-2">
                          <TypeIcon className="text-gray-600" fontSize="small" />
                          <Chip
                            label={(notification.type || 'unknown').replace(/_/g, ' ').toUpperCase()}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: '6.25%' }}>
                        <Box className="flex items-center space-x-2">
                          <LevelIcon fontSize="small" />
                          <Chip
                            label={(notification.level || 'info').toUpperCase()}
                            color={getLevelColor((notification.level || 'info').toLowerCase())}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: '6.25%' }}>{resolvedLocation}</TableCell>
                      <TableCell className="font-medium" sx={{ width: '75%' }}>
                        {notification.message}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No notifications match the current filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default NotificationsList;
